import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Video, VideoOff, Loader2, PhoneOff, Mic, MicOff, Users, Circle } from 'lucide-react';
import axios from 'axios';
import {
    LiveKitRoom,
    RoomAudioRenderer,
    useParticipants,
    useTracks,
    useLocalParticipant,
    VideoTrack,
    AudioTrack
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track, RoomEvent } from 'livekit-client';
import { ReactionBar, ReactionOverlay } from './ReactionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BroadcastView = ({
    socket,
    popupId,
    user,
    isAdmin,
    liveSession,
    onEndSession
}) => {
    const [token, setToken] = useState(null);
    const [serverUrl, setServerUrl] = useState(null);
    const [isConnecting, setIsConnecting] = useState(false);
    const [error, setError] = useState(null);
    const [isJoined, setIsJoined] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [recordingStartedAt, setRecordingStartedAt] = useState(null);
    const [reactions, setReactions] = useState([]);

    // Start backend recording (LiveKit Egress to S3)
    const startRecording = async () => {
        try {
            console.log('[BroadcastView] Starting LiveKit Egress recording...');
            const response = await axios.post(`${BACKEND_URL}/api/recording/start`, {
                room_name: popupId,
                popup_id: popupId
            });

            if (response.data.success) {
                setIsRecording(true);
                setRecordingStartedAt(new Date());
                console.log('[BroadcastView] Recording started, egress_id:', response.data.egress_id);
            }
        } catch (err) {
            console.error('[BroadcastView] Error starting recording:', err);
            console.warn('[BroadcastView] Recording unavailable, continuing without recording');
        }
    };

    // Stop backend recording and get S3 key
    const stopRecording = async () => {
        try {
            console.log('[BroadcastView] Stopping LiveKit Egress recording...');
            const response = await axios.post(`${BACKEND_URL}/api/recording/stop`, {
                popup_id: popupId
            });

            if (response.data.success) {
                console.log('[BroadcastView] Recording stopped, S3 key:', response.data.s3_key);
                return response.data.s3_key;
            }
        } catch (err) {
            console.error('[BroadcastView] Error stopping recording:', err);
        }
        return null;
    };

    // Fetch LiveKit token when joining
    const joinBroadcast = useCallback(async () => {
        setIsConnecting(true);
        setError(null);

        try {
            console.log('[BroadcastView] Getting token from backend...');
            const response = await axios.post(`${BACKEND_URL}/api/livekit/token`, {
                room_name: popupId,
                participant_name: user.username,
                participant_id: user.id,
                // Admin can publish, viewers can only subscribe
                can_publish: isAdmin,
                can_subscribe: true
            });

            if (!response.data.token || !response.data.serverUrl) {
                throw new Error('Invalid token response');
            }

            setToken(response.data.token);
            setServerUrl(response.data.serverUrl);
            setIsJoined(true);

            socket.emit('join_live_session', {
                popup_id: popupId,
                user_id: user.id
            });

            console.log('[BroadcastView] Successfully got token, connecting...');

            // Start recording for admin
            if (isAdmin) {
                setTimeout(() => startRecording(), 3000);
            }
        } catch (err) {
            console.error('[BroadcastView] Error getting token:', err);
            setError(err.response?.data?.detail || err.message || 'Failed to join broadcast');
        } finally {
            setIsConnecting(false);
        }
    }, [popupId, user, socket, isAdmin]);

    // Auto-join for admin
    useEffect(() => {
        if (isAdmin && !isJoined && !token) {
            joinBroadcast();
        }
    }, [isAdmin, isJoined, token, joinBroadcast]);

    // Handle reactions from socket
    useEffect(() => {
        const handleReaction = ({ emoji, username }) => {
            const newReaction = {
                id: Date.now() + Math.random(),
                emoji,
                username
            };
            setReactions(prev => [...prev, newReaction]);
        };

        socket.on('reaction_received', handleReaction);
        return () => socket.off('reaction_received', handleReaction);
    }, [socket]);

    const handleDisconnect = useCallback(async () => {
        console.log('[BroadcastView] Disconnecting...');

        let s3Key = null;
        if (isAdmin && isRecording) {
            s3Key = await stopRecording();
        }

        setToken(null);
        setServerUrl(null);
        setIsJoined(false);
        setIsRecording(false);

        socket.emit('leave_live_session', {
            popup_id: popupId,
            user_id: user.id
        });

        if (isAdmin) {
            onEndSession(s3Key);
        }
    }, [socket, popupId, user.id, isAdmin, onEndSession, isRecording]);

    const removeReaction = (id) => {
        setReactions(prev => prev.filter(r => r.id !== id));
    };

    const sendReaction = (emoji) => {
        socket.emit('send_reaction', {
            popup_id: popupId,
            emoji,
            user_id: user.id,
            username: user.username
        });
    };

    // Show join button for viewers
    if (!isJoined && !isAdmin) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl p-8">
                <div className="text-center mb-6">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#FFC857]/20 flex items-center justify-center">
                        <Video className="w-10 h-10 text-[#FFC857]" />
                    </div>
                    <h3 className="text-xl font-bold text-[#F5E3D7] mb-2">Live Broadcast</h3>
                    <p className="text-[#F5E3D7]/60">Join to watch the live stream</p>
                </div>
                {error && (
                    <p className="text-red-400 text-sm mb-4 text-center max-w-xs">{error}</p>
                )}
                <Button
                    onClick={joinBroadcast}
                    disabled={isConnecting}
                    className="bg-[#FFC857] text-[#2e0510] hover:bg-[#FFD87A] px-8 py-3"
                >
                    {isConnecting ? (
                        <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Connecting...
                        </>
                    ) : (
                        <>
                            <Video className="w-5 h-5 mr-2" />
                            Watch Broadcast
                        </>
                    )}
                </Button>
            </div>
        );
    }

    // Show loading
    if (isConnecting || !token || !serverUrl) {
        return (
            <div className="flex flex-col items-center justify-center h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl p-8">
                <Loader2 className="w-12 h-12 text-[#FFC857] animate-spin mb-4" />
                <p className="text-[#F5E3D7]">Connecting to broadcast...</p>
            </div>
        );
    }

    return (
        <div className="relative h-full bg-gradient-to-b from-[#1a0a0e] to-[#2e0510] rounded-xl overflow-hidden">
            <LiveKitRoom
                token={token}
                serverUrl={serverUrl}
                connect={true}
                video={isAdmin}
                audio={isAdmin}
                onDisconnected={handleDisconnect}
                style={{ height: '100%' }}
            >
                <BroadcastContent
                    isAdmin={isAdmin}
                    onLeave={handleDisconnect}
                    isRecording={isRecording}
                    recordingStartedAt={recordingStartedAt}
                    reactions={reactions}
                    onRemoveReaction={removeReaction}
                    onReact={sendReaction}
                />
            </LiveKitRoom>
        </div>
    );
};

// Inner component with LiveKit context
const BroadcastContent = ({
    isAdmin,
    onLeave,
    isRecording,
    recordingStartedAt,
    reactions,
    onRemoveReaction,
    onReact
}) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();

    const [isMicOn, setIsMicOn] = useState(true);
    const [isCameraOn, setIsCameraOn] = useState(true);
    const [recordingDuration, setRecordingDuration] = useState('00:00');

    // Find the broadcaster (admin) - they'll be the one publishing
    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );

    // Filter to get only the broadcaster's video track
    const broadcasterTrack = tracks.find(track =>
        track.participant.isLocal === isAdmin ||
        (!isAdmin && track.source === Track.Source.Camera && track.publication?.track)
    );

    // Update recording duration
    useEffect(() => {
        if (!isRecording || !recordingStartedAt) return;

        const interval = setInterval(() => {
            const seconds = Math.floor((new Date() - recordingStartedAt) / 1000);
            const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
            const secs = (seconds % 60).toString().padStart(2, '0');
            setRecordingDuration(`${mins}:${secs}`);
        }, 1000);

        return () => clearInterval(interval);
    }, [isRecording, recordingStartedAt]);

    const toggleMic = async () => {
        try {
            await localParticipant.setMicrophoneEnabled(!isMicOn);
            setIsMicOn(!isMicOn);
        } catch (err) {
            console.error('Error toggling mic:', err);
        }
    };

    const toggleCamera = async () => {
        try {
            await localParticipant.setCameraEnabled(!isCameraOn);
            setIsCameraOn(!isCameraOn);
        } catch (err) {
            console.error('Error toggling camera:', err);
        }
    };

    // Count viewers (non-admin participants)
    const viewerCount = participants.filter(p => !p.isLocal).length;

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="absolute top-4 left-4 z-10 flex items-center gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600 rounded-full">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                    <span className="text-white text-sm font-semibold">LIVE</span>
                </div>

                {isAdmin && (
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-[#2e0510]/80 rounded-full border border-[#3d1a24]">
                        <Users className="w-4 h-4 text-[#FFC857]" />
                        <span className="text-[#F5E3D7] text-sm">{viewerCount}</span>
                    </div>
                )}

                {isRecording && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-600/80 rounded-full">
                        <Circle className="w-3 h-3 text-white fill-white animate-pulse" />
                        <span className="text-white text-sm font-semibold">REC {recordingDuration}</span>
                    </div>
                )}
            </div>

            {/* Video Area */}
            <div className="flex-1 flex items-center justify-center p-4 pt-16 pb-20">
                {broadcasterTrack?.publication?.track ? (
                    <VideoTrack
                        trackRef={broadcasterTrack}
                        className="w-full max-h-[60vh] rounded-xl object-cover"
                    />
                ) : (
                    <div className="w-full max-h-[60vh] rounded-xl bg-black/50 flex items-center justify-center">
                        <p className="text-[#F5E3D7]/60">
                            {isAdmin ? 'Starting camera...' : 'Waiting for broadcaster...'}
                        </p>
                    </div>
                )}
            </div>

            {/* Reaction Overlay */}
            <ReactionOverlay reactions={reactions} onRemoveReaction={onRemoveReaction} />

            {/* Controls */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 p-3 bg-[#1a0a0e]/90 backdrop-blur-sm rounded-full border border-[#3d1a24]">
                {isAdmin ? (
                    <>
                        <Button
                            onClick={toggleMic}
                            className={`w-12 h-12 rounded-full transition-all ${isMicOn
                                    ? 'bg-[#3d1a24] hover:bg-[#4d2a34] text-[#F5E3D7]'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                        >
                            {isMicOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
                        </Button>
                        <Button
                            onClick={toggleCamera}
                            className={`w-12 h-12 rounded-full transition-all ${isCameraOn
                                    ? 'bg-[#3d1a24] hover:bg-[#4d2a34] text-[#F5E3D7]'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                        >
                            {isCameraOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
                        </Button>
                        <div className="w-px h-8 bg-[#3d1a24]" />
                        <Button
                            onClick={onLeave}
                            className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 text-white"
                        >
                            <PhoneOff className="w-5 h-5" />
                        </Button>
                    </>
                ) : (
                    <ReactionBar onReact={onReact} isLive={true} />
                )}
            </div>

            <RoomAudioRenderer />
        </div>
    );
};

export default BroadcastView;
